import type { SupplierTerm } from '@/lib/terminology/static-supplier-terminology'
import type { ReactNode } from 'react'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useOrganizationSettingsQuery } from '@/hooks/queries/useOrganizationSettingsQuery'
import { createLogger } from '@/lib/logger'
import {
  buildSupplierTerm,
  DEFAULT_MODE,
  isSupplierTerminologyMode,
  readCachedMode,
  setCurrentStaticSupplierTerminologyOrganization,
  subscribeSupplierTerminology,
  writeCachedMode,
} from '@/lib/terminology/static-supplier-terminology'
import { useOrganizationStore } from '@/store/organization.store'
import { useLocation } from 'react-router-dom'

import { ModuleTypeEnum } from '@scrambl/shared'

const logger = createLogger('contexts/SupplierTerminologyContext')

export {
  updateSupplierTerminology,
  getSupplierTerminologyStatic,
  setCurrentStaticSupplierTerminologyOrganization,
} from '@/lib/terminology/static-supplier-terminology'

const SupplierTerminologyContext = createContext<SupplierTerm | null>(null)

function readModeFromSettings(settings: unknown) {
  const mode = (settings as any)?.modules?.[ModuleTypeEnum.SUPPLIER]
    ?.terminology
  return isSupplierTerminologyMode(mode) ? mode : undefined
}

export function SupplierTerminologyProvider({
  children,
}: {
  children: ReactNode
}) {
  const currentOrganization = useOrganizationStore(
    state => state.currentOrganization,
  )
  const location = useLocation()
  const organizationId = currentOrganization?.id
  const isOnOrganizationsPage = location.pathname === '/organizations'
  const queryEnabled = !!organizationId && !isOnOrganizationsPage

  const { data: settingsData, isLoading } = useOrganizationSettingsQuery({
    enabled: queryEnabled,
  })
  const [version, setVersion] = useState(0)

  const settingsMode = readModeFromSettings(settingsData?.effectiveSettings)
  const organizationMode = readModeFromSettings(currentOrganization?.settings)
  const cachedMode = readCachedMode(organizationId)
  const mode = settingsMode ?? organizationMode ?? cachedMode ?? DEFAULT_MODE

  useEffect(() => {
    if (!organizationId) {
      setCurrentStaticSupplierTerminologyOrganization(null)
      return
    }

    setCurrentStaticSupplierTerminologyOrganization(organizationId)
  }, [organizationId])

  useEffect(() => {
    if (!organizationId || !settingsMode) return
    writeCachedMode(organizationId, settingsMode)
  }, [organizationId, settingsMode])

  useEffect(() => {
    const listener = (updatedOrganizationId: string) => {
      if (updatedOrganizationId === organizationId) {
        setVersion(current => current + 1)
      }
    }

    return subscribeSupplierTerminology(listener)
  }, [organizationId])

  const value = useMemo(
    () => buildSupplierTerm(mode, organizationId, isLoading),
    [mode, organizationId, isLoading, version],
  )

  return (
    <SupplierTerminologyContext.Provider value={value}>
      {children}
    </SupplierTerminologyContext.Provider>
  )
}

export function useSupplierTerminology() {
  const context = useContext(SupplierTerminologyContext)

  if (!context) {
    if (process.env.NODE_ENV === 'development') {
      logger.warn(
        'useSupplierTerminology called outside SupplierTerminologyProvider - returning defaults',
      )
    }

    return buildSupplierTerm(DEFAULT_MODE)
  }

  return context
}
